import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useAnimateCss, { useAnimateCssStagger } from "../../hooks/useAnimateCss";
import vImg1 from "../../assets/images/misc/man-in-location.webp";
import vImg2 from "../../assets/images/misc/billet-3.webp";
import vImg3 from "../../assets/images/misc/SDTF-1.webp";
import vImg4 from "../../assets/images/misc/steel-rebar-2.webp";
import heroVideo from "../../videos/MDS-حديد-المدينة.mp4";
import whySideImg from "../../assets/images/misc/steel-rebar-2.webp";
import iconIndustry from "../../assets/icons/INDUSTRY-EXPERTISE-icon.webp";
import iconQuality from "../../assets/icons/PREMIUM-QUALITY-CONTROL-icon.webp";
import iconTech from "../../assets/icons/ADVANCED-STEEL-TECHNOLOGY-icon.webp";
import iconCustomer from "../../assets/icons/CUSTOMER-FOCUSED-SERVICE-icon.webp";


const valuesImages = [vImg1, vImg2, vImg3, vImg4];

function ValuesCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % valuesImages.length), 5000);
    return () => clearInterval(t);
  }, []);

  const prev = () => setActive((i) => (i - 1 + valuesImages.length) % valuesImages.length);
  const next = () => setActive((i) => (i + 1) % valuesImages.length);

  const visible = [0, 1, 2].map((offset) => valuesImages[(active + offset) % valuesImages.length]);

  return (
    <div className="vcarousel">
      <button type="button" className="vcarousel-btn vcarousel-btn--prev" onClick={prev}>&#8249;</button>
      <div className="vcarousel-track">
        {visible.map((src, i) => (
          <img key={i} src={src} alt="" className="vcarousel-img" />
        ))}
      </div>
      <button type="button" className="vcarousel-btn vcarousel-btn--next" onClick={next}>&#8250;</button>
    </div>
  );
}

const features = [
  {
    icon: iconIndustry,
    titleKey: "feature-1-title",
    textKey: "feature-1-text",
  },
  { icon: iconQuality, titleKey: "feature-2-title", textKey: "feature-2-text" },
  { icon: iconTech, titleKey: "feature-3-title", textKey: "feature-3-text" },
  {
    icon: iconCustomer,
    titleKey: "feature-4-title",
    textKey: "feature-4-text",
  },
];

function WhyChooseUs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isAboutPage = pathname === "/about";
  const labelRef    = useAnimateCss("animate__fadeInDown", 0,   0.2);
  const videoRef    = useAnimateCss("animate__zoomIn",     150, 0.12, 800);
  const whyLabelRef = useAnimateCss("animate__fadeInDown", 0,   0.15);
  const whyHeadRef  = useAnimateCss("animate__fadeInUp",   120, 0.15);
  const cardsRef    = useAnimateCssStagger("animate__fadeInUp", 100, 0.08);
  const sideImgRef  = useAnimateCss("animate__fadeInRight",150, 0.12);

  return (
    <>
      {/* ── Video section ── */}
      <section className="why-section">
        <div className="container">
          <div className="text-center">
            <span className="why-label" ref={labelRef as any}>{t("why-label")}</span>
          </div>
          <div className="why-video-wrapper mt-4" ref={videoRef as any}>
            <video
              src={heroVideo}
              autoPlay
              loop
              muted
              playsInline
              controlsList="nodownload"
              disablePictureInPicture
              style={{
                width: "100%",
                borderRadius: "12px",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
        {/* ── Steel Advantages (About page only) ── */}
        {isAboutPage && (
          <section className="about-inner-section" style={{ paddingTop: "0" }}>
            <div className="container">
              <div className="row g-4">
                {(
                  [
                    { titleKey: "adv-1-title", textKey: "adv-1-text" },
                    { titleKey: "adv-2-title", textKey: "adv-2-text" },
                    { titleKey: "adv-3-title", textKey: "adv-3-text" },
                    { titleKey: "adv-4-title", textKey: "adv-4-text" },
                  ] as const
                ).map((card) => (
                  <div key={card.titleKey} className="col-lg-3 col-sm-6">
                    <div className="why-feature-card h-100">
                      <h3 className="why-feature-title">{t(card.titleKey)}</h3>
                      <p className="why-feature-text">{t(card.textKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {isAboutPage && (
          <div className="values-wrap">
            <div className="container">
              <div className="values-text-card">
                <span className="values-section-label">{t("our-values-label")}</span>
                <p>{t("our-values-p1")}</p>
                <p>{t("our-values-p2")}</p>
                <p>{t("our-values-p3")}</p>
              </div>
              <ValuesCarousel />
            </div>
          </div>
        )}
      </section>

      {/* ── Why Choose Us cards + image ── */}
      <section className="why-cards-section">
        <div className="container">
          <div className="text-center mb-4">
            <span className="why-cards-label" ref={whyLabelRef as any}>{t("Why Choose Us")}</span>
            <h2 className="why-cards-heading" ref={whyHeadRef as any}>{t("why-heading")}</h2>
          </div>

          <div className="row g-5 align-items-stretch">
            {/* LEFT — 2×2 feature grid (stagger each col-md-6) */}
            <div className="col-lg-7">
              <div className="row g-3" ref={cardsRef as any}>
                {features.map((f) => (
                  <div className="col-md-6" key={f.titleKey}>
                    <div className="why-feature-card">
                      <div className="why-feature-icon">
                        <img src={f.icon} alt={f.titleKey} />
                      </div>
                      <h4 className="why-feature-title">{t(f.titleKey)}</h4>
                      <p className="why-feature-text">{t(f.textKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — image */}
            <div className="col-lg-5 d-flex" ref={sideImgRef as any}>
              <img
                src={whySideImg}
                alt="Al Madina Steel"
                className="why-side-img"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default WhyChooseUs;
